import React, { useEffect } from 'react';
import Table from 'Components/Table';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { RoleService } from 'Services';
import useStickyTableHead from 'Hooks/useStickyTableHead';
import useSplitviewForm from 'Components/Table/hooks/useSplitViewForm';
import useGetRuleQuickFilters from 'Hooks/rule-quickfilters';
import { QF_MODIFIED, QF_STATUS } from 'Hooks/rule-quickfilters/helper';
import { useQueryParam } from 'Hooks/useQueryParam';
import { shouldShowExecutionOrder } from 'Selectors';
import { getRulePriority } from 'Hooks/query-hooks/helper';
import SplitViewContent from './SplitViewContent';

import styles from './MerchantRulesTable.css';

type Mode = 'sandbox' | 'deployed' | 'revisionWindow';

type Props = {
    globalRules: GlobalRulesData;
    merchantRules: MerchantRulesData;
    columnNames: Column<Rule>[];
    mode: Mode;
    tableName?: string;
    merchantId: string;
    deployedVersion?: number;
    isLoading?: boolean;
};

const TABLEID = 'merchant-rules-table';

const MerchantTable = ({
    merchantId,
    globalRules,
    merchantRules,
    columnNames,
    mode,
    tableName,
    deployedVersion,
    isLoading = false
}: Props) => {
    const { t } = useTranslation();
    useStickyTableHead(TABLEID);
    const showExecutionOrder = shouldShowExecutionOrder();
    const columns = React.useMemo<Column<Rule>[]>(() => columnNames, [columnNames]);
    const {
        splitViewFormState,
        setIsOpen,
        setInitialFormState,
        setRowId
    } = useSplitviewForm<MerchantRuleSplitViewFormState>();
    const readOnly = mode === 'deployed' || mode === 'revisionWindow';
    const updateSplitViewId = React.useCallback(
        (val) => {
            setRowId?.(val);
            setIsOpen?.(false);
        },
        [setRowId, setIsOpen]
    );
    const quickFilters = useGetRuleQuickFilters({
        quickFilters: mode === 'deployed' ? [QF_STATUS.id] : [QF_MODIFIED.id, QF_STATUS.id],
        initialFilters: [
            {
                id: QF_STATUS.id,
                activeId: readOnly ? [QF_STATUS.options.active] : []
            }
        ]
    });

    const {
        parsed: { selectedRowId }
    } = useQueryParam();

    useEffect(() => {
        setRowId?.(selectedRowId);
    }, [selectedRowId, setRowId]);

    const rulePriority = (rule: Rule) => {
        if (!rule?.priority) {
            return getRulePriority(t, rule);
        }
        return rule?.priority;
    };
    const merchantRulesData =
        merchantRules?.rules.map((rule) => {
            if (rule.inherit === 'MERCHANT') {
                return {
                    ...rule,
                    overrideGroup: t('Merchant Overrides'),
                    override: t('Merchant OVERRIDE').toUpperCase(),
                    priority: rulePriority(rule)
                };
            }
            return {
                ...rule,
                overrideGroup: t('Global Rules'),
                priority: rulePriority(rule)
            };
        }) || [];

    if (showExecutionOrder) {
        globalRules.rules.forEach((rule) => {
            if (!rule.priority) {
                rule.priority = getRulePriority(t, rule);
            }
        });
    }

    const data = React.useMemo(() => merchantRulesData, [merchantRulesData]);
    return (
        <div className={styles.tableContainer}>
            <Table
                tableName={tableName}
                isLoading={isLoading}
                showMultiSort
                showRichFilter
                manualSplitView
                controlledSplitViewId={splitViewFormState.rowId}
                updateSplitViewId={updateSplitViewId}
                showExpandableSearch
                icon={{
                    showIcon: true
                }}
                expand={{
                    canExpand: true,
                    expandAll: true
                }}
                initialState={{
                    groupBy: ['overrideGroup'],
                    sortBy: [
                        {
                            id: 'id',
                            desc: false
                        }
                    ],
                    globalFilter: {
                        quickFilters: quickFilters.initialQuickFilters
                    }
                }}
                dataTestId={TABLEID}
                columns={columns}
                noDataMessage='noMerchantRules'
                data={data}
                clickableRow
                splitViewContent={(props) => (
                    <SplitViewContent
                        revision={merchantRules.revision}
                        setRowId={setRowId}
                        setInitialFormState={setInitialFormState}
                        splitViewFormState={splitViewFormState}
                        setIsOpen={setIsOpen}
                        dataTestId='merchant'
                        globalRules={globalRules}
                        readOnly={readOnly || !RoleService.hasEditRuleOrBulkUploadAccess}
                        deploymentVersion={deployedVersion}
                        merchantId={merchantId}
                        mode={mode}
                        {...props}
                    />
                )}
                conditionalRowClasses={(row, tableStyle) => {
                    return clsx(row.original.inherit === 'MERCHANT' && tableStyle.bold);
                }}
                quickFilters={quickFilters.filters}
                dynamicGroupBy={['overrideGroup']}
            />
        </div>
    );
};

export default MerchantTable;
